import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { colors } from "../../../Shared/colors";
import { Skeleton } from "@mui/material";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

export const TelesalesProgressCharts = ({ dashboardData, isFetching }) => {
  const [chartHeight, setChartHeight] = useState(200);

  useEffect(() => {
    const updateChartHeight = () => {
      setChartHeight(window.innerWidth < 768 ? 150 : 200);
    };

    updateChartHeight();
    window.addEventListener('resize', updateChartHeight);
    return () => window.removeEventListener('resize', updateChartHeight);
  }, []);

  // Extract real data from API response
  const weeklyProgressData = useMemo(() => {
    if (!dashboardData || !dashboardData.weekly) return [];
    return dashboardData.weekly;
  }, [dashboardData]);

  const leadStatusData = useMemo(() => {
    if (!dashboardData || !dashboardData.leadStatus) return [];
    return dashboardData.leadStatus;
  }, [dashboardData]);

  const monthlyTrends = useMemo(() => {
    if (!dashboardData || !dashboardData.monthly) return [];
    return dashboardData.monthly;
  }, [dashboardData]);

  const hourlyPerformance = useMemo(() => {
    if (!dashboardData || !dashboardData.hourly) return [];
    return dashboardData.hourly;
  }, [dashboardData]);

  const performanceSummary = useMemo(() => {
    if (!dashboardData || !dashboardData.summary) {
      return {
        bestPerformingHour: "N/A",
        weeklyGoalProgress: "0% (0/80 calls)",
        conversionRateTrend: "0% this month",
        topLeadSource: "N/A (0%)"
      };
    }
    return dashboardData.summary;
  }, [dashboardData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipContainer>
          <TooltipLabel>{label}</TooltipLabel>
          {payload.map((entry, index) => (
            <TooltipItem key={index} color={entry.color}>
              {entry.name}: {entry.value}
            </TooltipItem>
          ))}
        </TooltipContainer>
      );
    }
    return null;
  };

  if (isFetching) {
    return (
      <ChartsSection>
        <SectionHeader>
          <h2>Performance Analytics</h2>
          <p>Your progress and trends</p>
        </SectionHeader>
        <ChartsGrid>
          <ChartCard>
            <Skeleton variant="rectangular" height={200} />
          </ChartCard>
          <ChartCard>
            <Skeleton variant="rectangular" height={200} />
          </ChartCard>
          <ChartCard>
            <Skeleton variant="rectangular" height={200} />
          </ChartCard>
          <ChartCard>
            <Skeleton variant="rectangular" height={200} />
          </ChartCard>
        </ChartsGrid>
      </ChartsSection>
    );
  }

  return (
    <ChartsSection>
      <SectionHeader>
        <h2>Performance Analytics</h2>
        <p>Your progress and trends</p>
      </SectionHeader>

      <ChartsGrid>
        {/* Weekly Progress Chart */}
        <ChartCard>
          <ChartTitle>This Week's Progress</ChartTitle>
          {weeklyProgressData.length === 0 ? (
            <NoDataMessage>No weekly data available yet</NoDataMessage>
          ) : (
            <ResponsiveContainer width="100%" height={chartHeight}>
              <AreaChart data={weeklyProgressData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="calls"
                  stackId="1"
                  stroke="#3b82f6"
                  fill="#3b82f6"
                  fillOpacity={0.6}
                  name="Calls"
                />
                <Area
                  type="monotone"
                  dataKey="connected"
                  stackId="2"
                  stroke="#10b981"
                  fill="#10b981"
                  fillOpacity={0.6}
                  name="Connected"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Lead Status Distribution */}
        <ChartCard>
          <ChartTitle>Lead Status Distribution</ChartTitle>
          {leadStatusData.length === 0 ? (
            <NoDataMessage>No lead data available</NoDataMessage>
          ) : (
            <ResponsiveContainer width="100%" height={chartHeight}>
              <PieChart>
                <Pie
                  data={leadStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={(entry) => `${entry.value}`}
                  labelLine={false}
                >
                  {leadStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  verticalAlign="bottom" 
                  height={36}
                  iconType="circle"
                  formatter={(value, entry) => `${value}: ${entry.payload.value}`}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Monthly Trends */}
        <ChartCard>
          <ChartTitle>Monthly Performance Trends</ChartTitle>
          {monthlyTrends.length === 0 ? (
            <NoDataMessage>No monthly data available yet</NoDataMessage>
          ) : (
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="calls"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: "#3b82f6", strokeWidth: 2, r: 4 }}
                  name="Calls"
                />
                <Line
                  type="monotone"
                  dataKey="meetings"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={{ fill: "#8b5cf6", strokeWidth: 2, r: 4 }}
                  name="Meetings"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        {/* Hourly Performance */}
        <ChartCard>
          <ChartTitle>Today's Hourly Performance</ChartTitle>
          {hourlyPerformance.length === 0 ? (
            <NoDataMessage>No activity today yet</NoDataMessage>
          ) : (
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={hourlyPerformance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="hour" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="calls" fill="#3b82f6" radius={[2, 2, 0, 0]} name="Calls" />
                <Bar dataKey="connected" fill="#10b981" radius={[2, 2, 0, 0]} name="Connected" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </ChartsGrid>

      {/* Performance Summary */}
      <PerformanceSummary>
        <SummaryTitle>Performance Summary</SummaryTitle>
        <SummaryGrid>
          <SummaryItem>
            <SummaryLabel>Best Performing Hour</SummaryLabel>
            <SummaryValue>{performanceSummary.bestPerformingHour}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Weekly Goal Progress</SummaryLabel>
            <SummaryValue>{performanceSummary.weeklyGoalProgress}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Conversion Rate Trend</SummaryLabel>
            <SummaryValue>{performanceSummary.conversionRateTrend}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>Top Lead Source</SummaryLabel>
            <SummaryValue>{performanceSummary.topLeadSource}</SummaryValue>
          </SummaryItem>
        </SummaryGrid>
      </PerformanceSummary>
    </ChartsSection>
  );
};

// Styled Components
const ChartsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (max-width: 768px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const SectionHeader = styled.div`
  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${colors.text};
    margin: 0 0 0.5rem 0;
  }
  
  p {
    color: #64748b;
    margin: 0;
    font-size: 0.95rem;
  }
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const ChartCard = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 8px;
  }
`;

const ChartTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${colors.text};
  margin: 0 0 1rem 0;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }
`;

const NoDataMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #64748b;
  font-size: 0.9rem;
  font-style: italic;
  
  @media (max-width: 768px) {
    height: 150px;
    font-size: 0.85rem;
  }
`;

const PerformanceSummary = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
    border-radius: 8px;
  }
`;

const SummaryTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${colors.text};
  margin: 0 0 1rem 0;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 0.5rem;
  }
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const SummaryItem = styled.div`
  background: ${colors.white};
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    border-radius: 4px;
  }
`;

const SummaryLabel = styled.div`
  font-size: 0.875rem;
  color: #64748b;
  margin-bottom: 0.25rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
  }
`;

const SummaryValue = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${colors.text};
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

const TooltipContainer = styled.div`
  background: ${colors.white};
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const TooltipLabel = styled.div`
  font-weight: 600;
  color: ${colors.text};
  margin-bottom: 0.5rem;
`;

const TooltipItem = styled.div`
  color: ${props => props.color || colors.text};
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

